import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

import { preSelectedIdsSelector, selectedItemsSelector } from '@air/redux-selected-items';

import { usePendingSelectionCommit } from '~/components/v2/components/private-selection-bar/hooks/usePendingSelectionCommit';
import { useViewsSelectAllStore } from '~/components/v2/store/features/views/useViewsSelectAllStore';
import { useWorkspaceRightPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceRightPanelStore';
import { Pathname } from '~/constants/pathname';
import { Routes } from '~/constants/routes';
import { hasOnlyCanvasesSelectedSelector } from '~/store/selectedItems/selectors';

export type UseSelectionBarVisibilityResult = {
  /** False where the bar has nothing worth acting on, regardless of what is selected. */
  canShowSelectionBar: boolean;
  /** `canShowSelectionBar` and something is actually selected. */
  hasSelection: boolean;
  /**
   * Replace-click mousedown has cleared `selectedItems` but mouseup has not committed the new
   * selection yet. Chrome should stay open and keep drawing the previous selection until then.
   */
  isPendingCommit: boolean;
  /** A canvas-only selection has followed the user somewhere canvases aren't first-class content. */
  hasStrandedCanvasSelection: boolean;
};

/**
 * When the selection bar has anything to say.
 *
 * Both bars answer this the same way, so the rule lives here rather than once in the standalone
 * bar's early return and again in the agent space's phase machine.
 */
export const useSelectionBarVisibility = (): UseSelectionBarVisibilityResult => {
  const { pathname } = useRouter();
  const selectedItems = useSelector(selectedItemsSelector);
  const preSelectedIds = useSelector(preSelectedIdsSelector);
  const hasOnlyCanvasesSelected = useSelector(hasOnlyCanvasesSelectedSelector);
  const isFullscreen = useWorkspaceRightPanelStore((state) => state.isFullscreen);
  const selectAll = useViewsSelectAllStore((state) => state.selectAll);

  // Canvas actions only mean anything where canvases are first-class content, so a canvas-only
  // selection elsewhere leaves the bar closed.
  const isBoardOrCanvasesPage =
    pathname === Pathname.BoardByBoardId || pathname === Pathname.BoardCanvas || pathname === Routes.canvases;
  const hasStrandedCanvasSelection = hasOnlyCanvasesSelected && !isBoardOrCanvasesPage;
  const canShowSelectionBar = !isFullscreen && !hasStrandedCanvasSelection;

  const { isPendingCommit } = usePendingSelectionCommit();

  // A replace click clears `selectedItems` on mousedown and only commits the new item on mouseup.
  // Treat an in-flight pre-selection as selection so the agent space does not collapse and reopen.
  const hasCommittedSelection = selectedItems.length > 0 || selectAll;
  const hasPendingSelection = preSelectedIds.length > 0;

  return {
    canShowSelectionBar,
    hasSelection: canShowSelectionBar && (hasCommittedSelection || hasPendingSelection),
    isPendingCommit,
    hasStrandedCanvasSelection,
  };
};
